import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableRow,
  TableColumnHeader,
  TableCell,
  TableRowHeader,
  PageSection
} from '@/common'
import { isNull } from 'es-toolkit'
import { ProductionRouteCreationState } from '@/features/production-routes/api/ProductionRouteModel'
import CnCodeTag from '@/components/CnCodeTag'

export default function ProductionRouteSummary({
  productionRoute
}: {
  productionRoute: ProductionRouteCreationState
}) {
  const { t } = useTranslation()
  const {
    aggregatedGood,
    assets,
    heatFlowSystem,
    isUsingWasteGas,
    storedCO2Handling,
    electricityProductionMethods,
    productionMethods
  } = productionRoute

  const assetsByEmissionPoint = useMemo(() => {
    return assets.reduce(
      (acc, asset) => {
        const emissionPoint = acc[asset.emissionPoint]
        if (emissionPoint) {
          emissionPoint.assets.push(asset)
        } else {
          acc[asset.emissionPoint] = { ...asset, assets: [asset] }
        }
        return acc
      },
      {} as Record<string, { assets: typeof assets }>
    )
  }, [assets])

  const assetsByEmissionsPointsEntries = Object.entries(assetsByEmissionPoint)
  const hasEmissionsPoints = assetsByEmissionsPointsEntries.length > 0

  return (
    <PageSection title={t('productionRoute:summary.title')}>
      <Table>
        <thead>
          <TableRow>
            <TableColumnHeader colSpan={2}>
              {t('productionRoute:steps.goodsAndProductionMethods')}
            </TableColumnHeader>
          </TableRow>
        </thead>
        <tbody>
          <TableRow>
            <TableRowHeader className="w-1/4 text-left">
              {t('productionRoute:goodsAndProductionMethods.general.aggregatedGoods.label')}
            </TableRowHeader>
            <TableCell>{t(`common:aggregatedGoods.${aggregatedGood}`)}</TableCell>
          </TableRow>
          <TableRow>
            <TableRowHeader className="text-left">
              {t('productionRoute:goodsAndProductionMethods.general.productionMethod.label')}
            </TableRowHeader>
            <TableCell>
              <div className="flex flex-col gap-2">
                {productionMethods.map(({ code, cnCodes }) => (
                  <div key={code} className="flex flex-wrap items-center gap-2">
                    {t(`common:productionMethods.${code}`)}:
                    {cnCodes.map(cnCode => (
                      <CnCodeTag key={cnCode} cnCode={cnCode} />
                    ))}
                  </div>
                ))}
              </div>
            </TableCell>
          </TableRow>
        </tbody>
      </Table>
      {hasEmissionsPoints && (
        <Table>
          <thead>
            <TableRow>
              <TableColumnHeader colSpan={2}>
                {t('productionRoute:steps.emissionPoints')}
              </TableColumnHeader>
            </TableRow>
          </thead>
          <tbody>
            {assetsByEmissionsPointsEntries.map(([emissionPoint, { assets }]) => {
              return (
                <TableRow key={emissionPoint}>
                  <TableRowHeader className="w-1/4 text-left">
                    {t(`emissionPoints:${emissionPoint}.title`)}
                  </TableRowHeader>
                  <TableCell>
                    <ul className="list-disc list-inside">
                      {assets.map(({ assetDefinitionId, customName, isCustom, quantity }) => (
                        <li key={assetDefinitionId}>
                          {quantity > 1 ? `${quantity} x ` : ''}
                          {isCustom ? customName : t(`assets:${assetDefinitionId}`)}
                        </li>
                      ))}
                    </ul>
                  </TableCell>
                </TableRow>
              )
            })}
          </tbody>
        </Table>
      )}

      <Table>
        <tbody>
          <TableRow>
            <TableRowHeader className="w-1/4 text-left">
              {t('productionRoute:heat.title')}
            </TableRowHeader>
            <TableCell>{t(`productionRoute:heat.options.${heatFlowSystem}.name`)}</TableCell>
          </TableRow>
          <TableRow>
            <TableRowHeader className="w-1/4 text-left">
              {t('productionRoute:electricity.title')}
            </TableRowHeader>
            <TableCell className="space-y-2">
              {electricityProductionMethods.map(({ type, isRenewable, info }) => {
                return (
                  <div key={type} className="border-b last:border-0 border-border-main pb-2">
                    {t(`common:electricitySources.${type}.title`)}
                    <p className="text-text-secondary text-sm">
                      {t('productionRoute:electricity.methodForm.isRenewable.label')}:{' '}
                      {!isNull(isRenewable) ? (isRenewable ? t('common:yes') : t('common:no')) : ''}
                    </p>
                    {info && (
                      <p className="text-text-secondary text-sm">
                        {t('productionRoute:electricity.methodForm.info.label')}: {info}
                      </p>
                    )}
                  </div>
                )
              })}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableRowHeader className="w-1/4 text-left">
              {t('productionRoute:wasteGas.title')}
            </TableRowHeader>
            <TableCell>{isUsingWasteGas ? t('common:yes') : t('common:no')}</TableCell>
          </TableRow>
          <TableRow>
            <TableRowHeader className="w-1/4 text-left">
              {t('productionRoute:storedCO2.title')}
            </TableRowHeader>
            <TableCell>
              {t(`productionRoute:storedCO2.options.${storedCO2Handling}.title`)}
            </TableCell>
          </TableRow>
        </tbody>
      </Table>
    </PageSection>
  )
}
